import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Typography,
  Paper,
  Box,
  Grid,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Toaster, toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import * as services from "./../../services/services";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export const ROLE = {
  ADMIN: 1,
  DOCTOR: 2,
};

export default function StaffCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const hospitalId = location.state?.hospitalId;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [hospitals, setHospitals] = useState([]);

  const [form, setForm] = useState({
    username: "",
    password: "",
    rePassword: "",
    name: "",
    lastname: "",
    title: "",
    phoneNumber: "",
    idCard: "",
    license_no: "",
    image: "",
    hospitalId: hospitalId,
    roleId: ROLE.DOCTOR,
  });

  const fetchHospital = useCallback(async () => {
    try {
      const res = await services.getHospitalList();
      setHospitals(res.data.data || []);
    } catch {
      toast.error("โหลดโรงพยาบาลไม่สำเร็จ");
    }
  }, []);

  useEffect(() => {
    fetchHospital();
  }, [fetchHospital]);

  const handleChange = async (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files?.[0]) {
      const file = files[0];
      setImagePreview(URL.createObjectURL(file));

      try {
        const res = await services.uploadImage(file);
        const imageUrl = res.data;
        setForm((prev) => ({
          ...prev,
          image: Array.isArray(imageUrl) ? imageUrl[0] : imageUrl,
        }));
      } catch {
        toast.error("Upload รูปไม่สำเร็จ");
      }
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    if (!form.username || !form.password) {
      toast.error("กรุณากรอก Username และ Password");
      return false;
    }

    if (form.password !== form.rePassword) {
      toast.error("รหัสผ่านไม่ตรงกัน");
      return false;
    }

    if(form.password.length < 8) {
      toast.error('รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร')
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);

      const payload = { ...form, isDisabled: 0 };
      const res = await services.register(payload);

      if (!res.data.success) throw new Error();

      toast.success("เพิ่มบุคลากรสำเร็จ");

      setTimeout(() => {
        navigate("/users", { state: { refresh: true } });
      }, 800);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("Email นี้ถูกใช้งานแล้ว");
      } else {
        toast.error("เพิ่มไม่สำเร็จ");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toaster position="bottom-left" />

      <Box sx={{ maxWidth: 1000, mx: "auto", mt: 3 }}>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={600} mb={3}>
            เพิ่มบุคลากร
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
              gap: 4,
            }}
          >
            {/* 🔥 รูป */}
            <Box
              sx={{
                border: "2px dashed #ddd",
                borderRadius: 3,
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                minHeight: 250,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fafafa",
              }}
              onClick={() => document.getElementById("upload-input")?.click()}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="preview"
                  style={{
                    width: 160,
                    height: 160,
                    objectFit: "cover",
                    borderRadius: 12,
                  }}
                />
              ) : (
                <Typography color="text.secondary">
                  คลิกเพื่ออัปโหลดรูป
                </Typography>
              )}

              <input
                id="upload-input"
                type="file"
                hidden
                accept="image/*"
                name="image"
                onChange={handleChange}
              />
            </Box>

            {/* 🔥 ฟอร์ม */}
            <Box>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      name="username"
                      value={form.username}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>

                  {/* PASSWORD */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      fullWidth
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="ยืนยัน Password"
                      type={showRePassword ? "text" : "password"}
                      name="rePassword"
                      value={form.rePassword}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>

                  {/* ROLE */}
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="สิทธิ์"
                      name="roleId"
                      value={form.roleId}
                      onChange={handleChange}
                      fullWidth
                    >
                      <MenuItem value={ROLE.ADMIN}>Admin</MenuItem>
                      <MenuItem value={ROLE.DOCTOR}>สัตวแพทย์</MenuItem>
                    </TextField>
                  </Grid>

                  {/* NAME */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="คำนำหน้า"
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      label="ชื่อ"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      label="นามสกุล"
                      name="lastname"
                      value={form.lastname}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>

                  {/* CONTACT */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="เบอร์โทร"
                      name="phoneNumber"
                      value={form.phoneNumber}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="เลขบัตรประชาชน"
                      name="idCard"
                      value={form.idCard}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="เลขที่ใบอนุญาต"
                      name="license_no"
                      value={form.license_no}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>

                  {/* HOSPITAL */}
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="โรงพยาบาล"
                      name="hospitalId"
                      value={form.hospitalId}
                      onChange={handleChange}
                      fullWidth
                      disabled
                    >
                      {hospitals.map((h) => (
                        <MenuItem key={h.id} value={h.id}>
                          {h.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* ACTION */}
                  <Grid item xs={12}>
                    <Box display="flex" gap={2}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate("/users")}
                        disabled={loading}
                      >
                        ยกเลิก
                      </Button>

                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                      >
                        {loading ? "กำลังบันทึก..." : "เพิ่มบุคลากร"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  );
}
